import { Icon } from "../components/common/Icon";
import { PageHero } from "../components/common/PageHero";
import { SmartLink } from "../components/common/SmartLink";
import { useDocumentMeta } from "../lib/useDocumentMeta";

const contactHref = "/contact";

const evidenceCards = [
  {
    icon: "document" as const,
    title: "Evidence assembly",
    copy: "Collect and present evidence references tied to scans, findings, remediation work, and operator decisions."
  },
  {
    icon: "shield" as const,
    title: "Endpoint remediation workflow",
    copy: "Support CKLB and SCAP-driven findings review with signed jobs, policy gates, and execution transcripts."
  },
  {
    icon: "users" as const,
    title: "Audit-facing operations",
    copy: "Give security, compliance, and assessor-facing stakeholders a clearer view of what changed and why."
  }
];

const guardrails = [
  "KyberDocs does not independently determine compliance",
  "Remediation actions remain signed, scoped, and reviewable",
  "Evidence should preserve lineage back to source artifacts",
  "Outputs support assessors and operators without replacing governance decisions"
];

export function KyberDocsPage() {
  useDocumentMeta({
    canonicalPath: "/kyber-docs",
    description:
      "KyberDocs is Holocron's evidence, endpoint remediation, and audit-readiness surface for regulated operations.",
    title: "KyberDocs | Holocron Security"
  });

  return (
    <>
      <PageHero
        actions={[
          { href: contactHref, label: "Request a KyberDocs Demo", variant: "primary" },
          { href: contactHref, label: "Build a CMMC Environment", variant: "secondary" }
        ]}
        copy="KyberDocs connects endpoint state, findings, remediation, and artifacts into audit-ready evidence workflows."
        eyebrow="KyberDocs"
        title="Prove the work."
      >
        <div className="device-panel-screen">
          <img alt="KyberDocs evidence and compliance interface" src="/assets/img/compliance-1.png" />
        </div>
      </PageHero>

      <section className="surface-dark">
        <div className="section-shell">
          <div className="section-intro">
            <p className="section-kicker">What it does</p>
            <h2 className="section-title">Evidence that stays connected to operations.</h2>
            <p className="section-copy">
              Compliance readiness depends on what teams can prove. KyberDocs gives operators a surface for findings,
              snapshots, remediation plans, transcripts, and audit-facing outputs.
            </p>
          </div>
          <div className="use-case-grid">
            {evidenceCards.map((card) => (
              <article key={card.title} className="use-case-card">
                <div className="icon-badge">
                  <Icon name={card.icon} />
                </div>
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-gradient surface-gradient--blue">
        <div className="section-shell split-layout">
          <div className="split-copy">
            <p className="section-kicker">Assessor-ready discipline</p>
            <h2 className="section-title">Evidence should be current, traceable, and reviewable.</h2>
            <div className="stack-copy">
              <p>
                KyberDocs supports regulated operators by keeping remediation and evidence workflows connected to the
                environment state they represent.
              </p>
              <p>
                The goal is not another report. The goal is a defensible operating record that helps teams understand
                what happened, what was fixed, and what still needs action.
              </p>
            </div>
          </div>
          <div className="fit-panel fit-panel--dark">
            <ul className="bullet-list">
              {guardrails.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="surface-dark cta-band">
        <div className="section-shell cta-center">
          <h2 className="section-title section-title--center">
            Pair KyberDocs with RapidFactory for a fuller CMMC operating model.
          </h2>
          <p>
            RapidFactory builds and maintains the regulated environment. KyberDocs supports the evidence and
            remediation workflow that keeps it defensible.
          </p>
          <div className="hero-actions hero-actions--centered">
            <SmartLink className="button primary" href={contactHref}>
              Request a Platform Demo
            </SmartLink>
            <SmartLink className="button secondary" href="/rapid-factory">
              Explore RapidFactory
            </SmartLink>
          </div>
        </div>
      </section>
    </>
  );
}
